import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Gift,
  Coins,
  Wallet,
  Clock,
  CheckCircle2,
  AlertCircle,
  X,
  RefreshCw,
  Copy,
  Check,
  TrendingUp,
  Sparkles,
  ArrowUpRight,
  Filter,
} from "lucide-react";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";

export default function PartnerGiftsModal({ isOpen, onClose, eventId = null }) {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [giftData, setGiftData] = useState({
    totalEarnings: 0,
    totalCoinValue: 0,
    totalPaidCoins: 0,
    paidCoinValue: 0,
    earnings: [],
  });
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState(null);

  const limit = 8;

  // Format date suffix (e.g., 15th September, 2026)
  const formattedAsOnDate = useMemo(() => {
    const today = new Date();
    const day = today.getDate();
    const getSuffix = (d) => {
      if (d >= 11 && d <= 13) return "th";
      switch (d % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };
    const monthYear = today.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });
    return `As on ${day}${getSuffix(day)} ${monthYear}`;
  }, []);

  const fetchGifts = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) {
        setLoading(true);
        setSkip(0);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const currentSkip = isRefresh ? 0 : skip;

      try {
        const url = API_ENDPOINTS.PARTNER_GIFTS_PAGINATED(
          currentSkip,
          limit,
          eventId
        );
        const res = await fetchWithAuth(url);
        if (!res.ok) {
          throw new Error(`Failed to load gifts: ${res.status}`);
        }
        const data = await res.json();

        setGiftData((prev) => {
          if (isRefresh) {
            return {
              totalEarnings: data.totalEarnings || 0,
              totalCoinValue: data.totalCoinValue || 0,
              totalPaidCoins: data.totalPaidCoins || 0,
              paidCoinValue: data.paidCoinValue || 0,
              earnings: data.earnings || [],
            };
          } else {
            return {
              ...prev,
              totalEarnings: data.totalEarnings || prev.totalEarnings,
              totalCoinValue: data.totalCoinValue || prev.totalCoinValue,
              totalPaidCoins: data.totalPaidCoins || prev.totalPaidCoins,
              paidCoinValue: data.paidCoinValue || prev.paidCoinValue,
              earnings: [...prev.earnings, ...(data.earnings || [])],
            };
          }
        });

        const newItemsCount = (data.earnings || []).length;
        if (newItemsCount < limit) {
          setHasMore(false);
        } else {
          setHasMore(true);
          setSkip(currentSkip + newItemsCount);
        }
      } catch (err) {
        console.error("Partner gifts fetch error:", err);
        setError(err.message || "Failed to load gifts");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [skip, limit, eventId]
  );

  useEffect(() => {
    if (isOpen) {
      fetchGifts(true);
    }
  }, [isOpen]);

  const handleCopyTransaction = (txId) => {
    if (!txId) return;
    navigator.clipboard.writeText(txId);
    setCopiedId(txId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Recent";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  // Filtered earnings
  const filteredEarnings = useMemo(() => {
    const list = giftData.earnings || [];
    switch (activeFilter) {
      case "direct":
        return list.filter((e) => e.isDirectTip === true);
      case "coins":
        return list.filter((e) => !e.isDirectTip);
      case "paid":
        return list.filter((e) => e.isPaid === true);
      case "pending":
        return list.filter((e) => !e.isPaid);
      default:
        return list;
    }
  }, [giftData.earnings, activeFilter]);

  if (!isOpen || typeof document === "undefined") return null;

  const toBePaidCoins = Math.max(
    0,
    giftData.totalEarnings - giftData.totalPaidCoins
  );
  const toBePaidAmount = Math.max(
    0,
    giftData.totalCoinValue - giftData.paidCoinValue
  );

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", duration: 0.4 }}
          className="relative w-full max-w-2xl max-h-[92vh] overflow-hidden rounded-3xl bg-white shadow-2xl border border-purple-100 flex flex-col"
        >
          {/* Top Header */}
          <div className="p-5 sm:p-6 border-b border-purple-100/80 bg-gradient-to-r from-purple-50 via-white to-pink-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-[#72288D] to-[#9C38BF] flex items-center justify-center text-white shadow-md shadow-purple-500/20">
                <Gift className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
                  <span>Fan Tips & Gifts</span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-[#72288D]">
                    Itemized Breakdown
                  </span>
                </h2>
                <p className="text-xs text-gray-500 font-medium">{formattedAsOnDate}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fetchGifts(true)}
                title="Refresh Gifts"
                className="p-2 text-gray-400 hover:text-[#72288D] hover:bg-purple-50 rounded-xl transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
            {/* Totals Summary Cards (Matches Android PartnerGifts cvBanner) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Total Coins Received */}
              <div className="rounded-2xl p-4 bg-gradient-to-br from-purple-50 to-white border border-purple-200/80 shadow-xs relative overflow-hidden">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Total Received
                  </span>
                  <div className="w-7 h-7 rounded-xl bg-purple-100 text-[#72288D] flex items-center justify-center">
                    <Coins className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {giftData.totalEarnings.toLocaleString()}{" "}
                  <span className="text-sm font-semibold text-gray-500">Coins</span>
                </div>
                <div className="text-xs font-semibold text-[#72288D] mt-1 flex items-center gap-1">
                  <span>Worth ₹{giftData.totalCoinValue.toFixed(2)}</span>
                </div>
              </div>

              {/* Total Paid */}
              <div className="rounded-2xl p-4 bg-gradient-to-br from-emerald-50 to-white border border-emerald-200/80 shadow-xs relative overflow-hidden">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Total Paid
                  </span>
                  <div className="w-7 h-7 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                    <Wallet className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {giftData.totalPaidCoins.toLocaleString()}{" "}
                  <span className="text-sm font-semibold text-gray-500">Coins</span>
                </div>
                <div className="text-xs font-semibold text-emerald-600 mt-1">
                  Paid ₹{giftData.paidCoinValue.toFixed(2)}
                </div>
              </div>

              {/* Remaining / To Be Paid */}
              <div className="rounded-2xl p-4 bg-gradient-to-br from-amber-50 to-white border border-amber-200/80 shadow-xs relative overflow-hidden">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    To Be Paid
                  </span>
                  <div className="w-7 h-7 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
                    <Clock className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {toBePaidCoins.toLocaleString()}{" "}
                  <span className="text-sm font-semibold text-gray-500">Coins</span>
                </div>
                <div className="text-xs font-semibold text-amber-600 mt-1">
                  Pending ₹{toBePaidAmount.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Filter Pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide text-xs font-semibold">
              {[
                { id: "all", label: "All Activity" },
                { id: "direct", label: "Direct Tips" },
                { id: "coins", label: "Virtual Gifts" },
                { id: "paid", label: "Settled / Paid" },
                { id: "pending", label: "Pending Payout" },
              ].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setActiveFilter(f.id)}
                  className={`px-3 py-1.5 rounded-xl transition-all whitespace-nowrap ${
                    activeFilter === f.id
                      ? "bg-[#72288D] text-white shadow-xs"
                      : "bg-gray-100 text-gray-600 hover:bg-purple-50 hover:text-[#72288D]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Content States */}
            {loading ? (
              <div className="py-16 flex flex-col items-center justify-center space-y-3">
                <div className="w-9 h-9 border-3 border-[#72288D] border-t-transparent rounded-full animate-spin" />
                <p className="text-xs font-medium text-gray-500">Fetching gifts and tips...</p>
              </div>
            ) : error ? (
              <div className="rounded-2xl p-6 bg-red-50 text-center border border-red-100">
                <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm font-bold text-red-700">{error}</p>
                <button
                  type="button"
                  onClick={() => fetchGifts(true)}
                  className="mt-3 px-4 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors"
                >
                  Retry Loading
                </button>
              </div>
            ) : filteredEarnings.length === 0 ? (
              <div className="rounded-3xl p-10 text-center bg-gray-50/70 border border-dashed border-gray-200">
                <div className="w-14 h-14 rounded-full bg-purple-100 text-[#72288D] flex items-center justify-center mx-auto mb-3">
                  <Gift className="w-7 h-7" />
                </div>
                <h4 className="text-base font-bold text-gray-800">No Gifts Found</h4>
                <p className="text-xs text-gray-500 max-w-xs mx-auto mt-1">
                  {activeFilter === "all"
                    ? "Fans and attendees can send direct tips and virtual gift coins to you during live performances."
                    : `No records matching the "${activeFilter}" filter.`}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredEarnings.map((earning, idx) => (
                  <motion.div
                    key={earning.transactionId || idx}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    className="p-4 rounded-2xl bg-white border border-purple-50/80 hover:border-purple-200 shadow-xs hover:shadow-md transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                  >
                    {/* Left details */}
                    <div className="flex items-center gap-3.5">
                      <div
                        className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${
                          earning.isDirectTip
                            ? "bg-amber-100 text-amber-600"
                            : "bg-purple-100 text-[#72288D]"
                        }`}
                      >
                        {earning.isDirectTip ? (
                          <TrendingUp className="w-5 h-5" />
                        ) : (
                          <Coins className="w-5 h-5" />
                        )}
                      </div>

                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-extrabold text-gray-900">
                            {earning.isDirectTip
                              ? `₹${earning.amount || 0}`
                              : `${earning.coins} ${
                                  earning.coins === 1 ? "Coin" : "Coins"
                                }`}
                          </span>

                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                              earning.isDirectTip
                                ? "bg-amber-100 text-amber-800"
                                : "bg-purple-100 text-purple-800"
                            }`}
                          >
                            {earning.isDirectTip ? "Direct Tip" : "Virtual Gift"}
                          </span>
                        </div>

                        {!earning.isDirectTip && (
                          <p className="text-xs font-medium text-gray-500">
                            Worth ₹{earning.coinValue}
                          </p>
                        )}

                        <div className="flex items-center gap-2 text-[11px] text-gray-400">
                          <Clock className="w-3 h-3" />
                          <span>{formatDate(earning.createdAt)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Right status & transaction info */}
                    <div className="flex sm:flex-col items-center sm:items-end justify-between border-t sm:border-t-0 pt-2 sm:pt-0 border-gray-100">
                      <span
                        className={`text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${
                          earning.isPaid
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {earning.isPaid ? (
                          <>
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span>Paid</span>
                          </>
                        ) : (
                          <>
                            <Clock className="w-3.5 h-3.5" />
                            <span>Pending Payout</span>
                          </>
                        )}
                      </span>

                      {earning.transactionId && (
                        <button
                          type="button"
                          onClick={() => handleCopyTransaction(earning.transactionId)}
                          className="mt-1.5 text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-1 font-mono transition-colors"
                          title="Copy Transaction ID"
                        >
                          <span>
                            ID: {earning.transactionId.slice(0, 8)}...
                          </span>
                          {copiedId === earning.transactionId ? (
                            <Check className="w-3 h-3 text-emerald-600" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}

                {/* Pagination Load More */}
                {hasMore && (
                  <div className="pt-2 text-center">
                    <button
                      type="button"
                      disabled={loadingMore}
                      onClick={() => fetchGifts(false)}
                      className="px-5 py-2.5 rounded-xl bg-purple-50 text-[#72288D] border border-purple-200 hover:bg-purple-100 text-xs font-bold transition-all disabled:opacity-50 flex items-center gap-2 mx-auto"
                    >
                      {loadingMore ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-[#72288D] border-t-transparent rounded-full animate-spin" />
                          <span>Loading more...</span>
                        </>
                      ) : (
                        <span>Load More Gifts</span>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#72288D]" />
              <span>Gifts and direct tips are credited automatically to your account</span>
            </span>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300 font-semibold transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}
